<template>
  <div class="inner-vacancy-application">
    <vtitle />

    <div class="application">
      <div class="application__head">
        <vinput
          v-model="name"
          class="application__title-input"
          :placeholder="$tr('vacancy.preferences.application.description.placeholder')"
        />

        <vtextarea
          v-model="description"
          class="application__description"
          :height="84"
          :placeholder="$tr('vacancy.videointerview.form.description.placeholder')"
        />
      </div>

      <question-list v-if="application" :questions="questions" @set-questions="questions = $event" />

      <div class="row-block button-wrapper">
        <vbutton label="vacancy.videoInterview.add" type="add" @click="add_question" />

        <vbutton
          class="vacancy-button"
          :loading="loading"
          :disabled="is_disabled || loading"
          label="common.save"
          @click="save()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import VueScrollTo from 'vue-scrollto';
import QuestionList from './components/List';
import vtitle from '@/routes_new/inner/vacancy/components/vtitle';

export default {
  name: 'InnerVacancyApplication',

  components: {
    QuestionList,
    vtitle
  },

  props: {
    vacancyId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false
    };
  },

  computed: {
    ...mapState('company', { companyId: state => state.company.id }),

    ...mapGetters('vacancy/application', ['isSomeListsEmpty']),

    application() {
      return this.$store.getters['vacancy/application/list'];
    },

    name: {
      get() {
        return this.$store.getters['vacancy/application/name'];
      },

      set(val) {
        this.$store.commit('vacancy/application/set_name', val);
      }
    },

    description: {
      get() {
        return this.$store.getters['vacancy/application/description'];
      },

      set(val) {
        this.$store.commit('vacancy/application/set_description', val);
      }
    },

    questions: {
      get() {
        return this.application.questions;
      },

      set(questions) {
        this.$store.commit('vacancy/application/update_questions', questions);
      }
    },

    modules() {
      return this.$store.getters['vacancy_new/modules/list'];
    },

    is_module_videointerview_active() {
      if (!this.modules || !this.modules.length) {
        return false;
      }

      return this.modules.find(item => item.id === 'videointerview').is_active;
    },

    is_module_test_active() {
      if (!this.modules || !this.modules.length) {
        return false;
      }

      return this.modules.find(item => item.id === 'test').is_active;
    },

    next_section() {
      if (this.is_module_test_active) {
        return 'tests';
      } else if (this.is_module_videointerview_active) {
        return 'video';
      } else {
        return 'competencies';
      }
    },

    empty_questions() {
      return this.application ? this.application.questions.some(el => el.name.length === 0) : false;
    },

    invalidDateQuestions() {
      if (!this.application || !this.application.questions || !this.application.questions.length) {
        return false;
      }
      const isInvalidDates =
        !!this.application.questions.some(el => el.isDateValidMin === false) ||
        !!this.application.questions.some(el => el.isDateValidMax === false);
      const emptyDates = !!this.application.questions.some(el => {
        return el.is_dropout_date === true && !el.dropout_date_min && !el.dropout_date_max;
      });

      return isInvalidDates || emptyDates;
    },

    noAnswers() {
      if (!this.application || !this.application.questions || !this.application.questions.length) {
        return false;
      }

      let noAnswers = false;

      this.application.questions.forEach(question => {
        if (
          (question.type === 'single' || question.type === 'multiple') &&
          (!question.answers || !question.answers.length)
        ) {
          noAnswers = true;
        }
      });
      return noAnswers;
    },

    answers_empty() {
      return this.$store.getters['vacancy/application/answers_empty'] || this.noAnswers;
    },

    is_disabled() {
      return this.empty_questions || this.answers_empty || this.invalidDateQuestions || this.isSomeListsEmpty;
    }
  },

  async created() {
    await this.load();
    await this.fetchEnumerateLists(this.companyId);
  },

  beforeDestroy() {
    this.$store.dispatch('vacancy/application/clear');
  },

  methods: {
    ...mapActions('vacancy/application', ['fetchTests', 'fetchEnumerateLists']),

    async load() {
      let survey = this.modules.filter(item => item.id === 'survey')[0];
      let show = this.vacancyId && survey && survey.entity_id;
      show &&
        (await this.$store.dispatch('vacancy/application/load', {
          app_id: survey.entity_id,
          vacancy_id: this.vacancyId
        }));
    },

    add_question() {
      this.$store.commit('vacancy/application/add_question');
      VueScrollTo.scrollTo(document.querySelector('.button-wrapper'), 900);
    },

    async save() {
      this.loading = true;

      let survey = JSON.parse(JSON.stringify(this.application));

      if (survey.questions && survey.questions.length > 0) {
        survey.questions.forEach((item, index) => {
          // console.log('item.type', item.type);
          item.sort_order = index;
          if (item.is_new) {
            delete item.id;
            delete item.is_new;
          }
          if (item.answers && item.answers.length > 0) {
            if (item.type === 'open' || item.type === 'date' || item.type === 'file') {
              item.answers = [];
            } else {
              item.answers.forEach((answer, ind) => {
                answer.sort_order = ind;
                if (/^new_[0-9]+$/.test(answer.id)) {
                  delete answer.id;
                }
              });
            }
          }
        });
      }

      await this.$store
        .dispatch('vacancy/application/update', survey)
        .then(() => {
          this.set_changes(false);
          this.$noty.success(this.$tr('vacancy.preferences.modals.saveSuccess.text'));
          // if (is_continue) {
          //   this.$router.push({
          //     path: `/vacancies/${this.vacancyId}/${this.next_section}`
          //   });
          // }
        })
        .catch(() => {
          this.set_changes(false);
          this.$noty.error(this.$tr('vacancy.preferences.modals.saveFailure.text'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    set_changes(v) {
      this.$store.commit('vacancy/application/set_changes', v);
    }
  }
};
</script>

<style lang="scss" scoped>
.inner-vacancy-application {
  max-width: 700px;
}

.application {
  margin-top: 4px;
  margin-bottom: 140px;

  &__title {
    color: #233346;
    font-size: 15px;
    line-height: 22px;
  }

  &__title-input {
    margin-bottom: 29px;
  }

  &__description {
    margin-bottom: 40px;
  }
}

.row-block {
  margin-bottom: 50px;
}

.button-wrapper {
  display: flex;
  justify-content: space-between;
  margin-top: 50px;

  .vacancy-button:not(:last-child) {
    margin-right: 10px;
  }
}

.application__title-input {
  margin-top: -6px;
}

::v-deep .application__description {
  &.vtextarea {
    textarea {
      padding: 20px 16px;
    }
  }
}
</style>
